/**
 * Ruta de Cobranza (`/collections`). La pestaña y el filtro de gestor de la
 * cola viven en la URL para recordarlos al volver y poder compartirlos.
 */
import { useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { CollectionsPage } from '@/features/collections/collections-page'
import { COLLECTIONS_TABS, type CollectionsTab } from '@/features/collections/constants'
import type { CollectorFilter } from '@/features/collections/queue-scope'
import { requirePermission } from '@/lib/route-guards'

/**
 * `?tab=` recuerda la pestaña (cola de mora / promesas) y `?collector=` el
 * filtro de gestor (`mine`, `all`, `unassigned` o el id de un gestor).
 */
const collectionsSearchSchema = z.object({
  tab: z.enum(COLLECTIONS_TABS).optional().catch(undefined),
  collector: z.string().trim().min(1).optional().catch(undefined),
})

/** Cobranza: cola de mora, gestiones y promesas. */
export const Route = createFileRoute('/_app/collections/')({
  beforeLoad: requirePermission('collections:read'),
  validateSearch: collectionsSearchSchema,
  component: CollectionsRoute,
})

function CollectionsRoute() {
  const { tab, collector } = Route.useSearch()
  const navigate = Route.useNavigate()
  const onTabChange = useCallback(
    (next: CollectionsTab) => void navigate({ search: (previous) => ({ ...previous, tab: next }), replace: true }),
    [navigate],
  )
  const onCollectorChange = useCallback(
    (next: CollectorFilter) => void navigate({ search: (previous) => ({ ...previous, collector: next }), replace: true }),
    [navigate],
  )
  return <CollectionsPage tab={tab ?? 'queue'} onTabChange={onTabChange} collector={collector} onCollectorChange={onCollectorChange} />
}
